import { useCallback, useEffect, useMemo, useState } from 'react';

const ACCEPTED_EXT = /\.(pdf|ai|eps|psd|jpe?g|png|svg)$/i;

/**
 * useArtworkFiles — owns the artwork-file collection logic for the
 * UploadArtwork page.
 *
 * Responsibilities:
 *   - File state + add/remove operations
 *   - Drag-and-drop event handlers (with isDragActive flag)
 *   - File-extension validation (mirrors the <input accept> attribute
 *     since drag-and-drop bypasses that filter)
 *   - Browser-side ObjectURL thumbnails for raster files (for the
 *     PageMakerEditor previews) — auto-revoked when the list changes
 *
 * Returns a flat object — destructure only what you need at the call site.
 */
export const useArtworkFiles = () => {
  const [files, setFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const addFiles = useCallback((incoming) => {
    if (!incoming || !incoming.length) return;
    const valid = [];
    const rejected = [];
    incoming.forEach((f) => {
      if (ACCEPTED_EXT.test(f.name || '')) valid.push(f);
      else rejected.push(f.name || 'unknown');
    });
    if (rejected.length) {
      // eslint-disable-next-line no-alert
      alert(
        `These files were skipped (unsupported format):\n${rejected.join(
          '\n',
        )}\n\nSupported: PDF, AI, EPS, PSD, JPG, PNG, SVG`,
      );
    }
    if (valid.length) setFiles((prev) => [...prev, ...valid]);
  }, []);

  const handleFileSelect = useCallback(
    (e) => {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
      // Reset so re-selecting the same file fires onChange again.
      e.target.value = '';
    },
    [addFiles],
  );

  const removeFile = useCallback((index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Drag-and-drop handlers — drop must be intercepted or the browser
  // navigates away to display the file (which felt like a broken upload).
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);
  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);
  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);
  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      const dropped = Array.from(e.dataTransfer?.files || []);
      addFiles(dropped);
    },
    [addFiles],
  );

  // Browser-side thumbnail URLs for the PageMakerEditor previews.
  // Raster formats use Object URLs; vector formats fall back to coloured
  // swatches (the editor handles `thumbUrl = null` gracefully).
  const filePreviews = useMemo(() => {
    const out = [];
    files.forEach((f, idx) => {
      const isRaster =
        /^image\/(png|jpeg|jpg|gif|webp)$/i.test(f.type) ||
        /\.(png|jpe?g|gif|webp)$/i.test(f.name || '');
      if (isRaster) {
        // eslint-disable-next-line no-undef
        out.push({ idx, thumbUrl: URL.createObjectURL(f) });
      } else {
        out.push({ idx, thumbUrl: null });
      }
    });
    return out;
  }, [files]);

  // Revoke ObjectURLs when the file list changes.
  useEffect(() => {
    return () => {
      filePreviews.forEach((p) => {
        // eslint-disable-next-line no-undef
        if (p.thumbUrl) URL.revokeObjectURL(p.thumbUrl);
      });
    };
  }, [filePreviews]);

  return {
    files,
    setFiles,
    addFiles,
    handleFileSelect,
    removeFile,
    filePreviews,
    isDragActive,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
  };
};

export default useArtworkFiles;
